import { useState, useCallback, useRef } from 'react'
import type {
  ZoningQACitation,
  ZoningQALanguage,
  ZoneDocumentFilter,
} from '@popety-io/types'
import type { ZoneQAMessage, ZoneQAContext, ZoneQAService } from './types'

interface UseZoneQAOptions {
  zoneContext: ZoneQAContext
  zoningService: ZoneQAService
  language: ZoningQALanguage
  /** Document filters for chunk retrieval - filters by specific document IDs */
  documentFilters?: ZoneDocumentFilter[]
}

interface UseZoneQAReturn {
  messages: ZoneQAMessage[]
  isStreaming: boolean
  error: string | null
  isOpen: boolean
  sessionId: string | null
  sendMessage: (message: string) => Promise<void>
  toggleOpen: () => void
  clearError: () => void
  clearMessages: () => void
  submitFeedback: (
    messageId: string,
    rating: number,
    feedbackType?:
      | 'helpful'
      | 'not_helpful'
      | 'incorrect'
      | 'incomplete'
      | 'other',
    comment?: string
  ) => Promise<void>
}

/**
 * Hook for managing Zone Q&A state and streaming
 */
export const useZoneQA = ({
  zoneContext,
  zoningService,
  language,
  documentFilters,
}: UseZoneQAOptions): UseZoneQAReturn => {
  const [messages, setMessages] = useState<ZoneQAMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)

  // Use ref to track if stream should be aborted
  const abortRef = useRef(false)

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
    setSessionId(null)
    setError(null)
  }, [])

  const sendMessage = useCallback(
    async (messageText: string) => {
      if (!messageText.trim() || isStreaming) return

      const trimmedMessage = messageText.trim()
      abortRef.current = false

      // Add user message immediately
      const userMessage: ZoneQAMessage = {
        id: `user_${Date.now()}`,
        role: 'user',
        content: trimmedMessage,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, userMessage])
      setIsStreaming(true)
      setError(null)

      // Prepare assistant message placeholder
      let assistantMessageId = `assistant_${Date.now()}`
      let assistantContent = ''
      const assistantCitations: ZoningQACitation[] = []

      try {
        // Create assistant message placeholder
        const assistantMessage: ZoneQAMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          citations: [],
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, assistantMessage])

        // Start streaming
        const stream = zoningService.askZoneStream(
          'general',
          zoneContext.zoneId,
          {
            sessionId: sessionId || undefined,
            message: trimmedMessage,
            language,
            zoneContext: {
              zone_id: zoneContext.zoneId,
              zone_name: zoneContext.zoneName,
              municipality_id: zoneContext.municipalityId,
              municipality_name: zoneContext.municipalityName,
              region_id: zoneContext.regionId,
              region_name: zoneContext.regionName,
            },
            documentFilters,
          }
        )

        for await (const chunk of stream) {
          if (abortRef.current) break

          switch (chunk.type) {
            case 'session':
              if (chunk.sessionId) {
                setSessionId(chunk.sessionId)
              }
              break

            case 'citation':
              if (chunk.citation) {
                assistantCitations.push(chunk.citation)
                setMessages((prev) => {
                  const msgIndex = prev.findIndex(
                    (msg) => msg.id === assistantMessageId
                  )
                  if (msgIndex >= 0) {
                    // Update existing message
                    return prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, citations: [...assistantCitations] }
                        : msg
                    )
                  }
                  // Message not yet in state, add it with citations
                  return [
                    ...prev,
                    {
                      id: assistantMessageId,
                      role: 'assistant' as const,
                      content: assistantContent,
                      citations: [...assistantCitations],
                      timestamp: new Date(),
                    },
                  ]
                })
              }
              break

            case 'content':
              if (chunk.text) {
                assistantContent += chunk.text
                setMessages((prev) => {
                  const msgIndex = prev.findIndex(
                    (msg) => msg.id === assistantMessageId
                  )
                  if (msgIndex >= 0) {
                    // Update existing message
                    return prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, content: assistantContent }
                        : msg
                    )
                  }
                  // Message not yet in state, add it with content
                  return [
                    ...prev,
                    {
                      id: assistantMessageId,
                      role: 'assistant' as const,
                      content: assistantContent,
                      citations: [...assistantCitations],
                      timestamp: new Date(),
                    },
                  ]
                })
              }
              break

            case 'suggest_docs':
              setMessages((prev) => {
                const msgIndex = prev.findIndex(
                  (msg) => msg.id === assistantMessageId
                )
                if (msgIndex >= 0) {
                  return prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, suggestDocs: true }
                      : msg
                  )
                }
                // Message not yet in state, add it with suggestDocs
                return [
                  ...prev,
                  {
                    id: assistantMessageId,
                    role: 'assistant' as const,
                    content: assistantContent,
                    citations: [...assistantCitations],
                    suggestDocs: true,
                    timestamp: new Date(),
                  },
                ]
              })
              break

            case 'done':
              // Update message with final server ID for feedback
              if (chunk.messageId) {
                const serverMessageId = chunk.messageId
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, id: serverMessageId }
                      : msg
                  )
                )
                assistantMessageId = serverMessageId
              }
              break

            case 'error':
              setError(chunk.error || 'An error occurred')
              // Remove the assistant placeholder if error
              setMessages((prev) =>
                prev.filter((msg) => msg.id !== assistantMessageId)
              )
              break
          }
        }
      } catch (err) {
        console.error('[ZoneQA] Stream error:', err)
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred'
        setError(errorMessage)
        // Remove the assistant placeholder on error
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== assistantMessageId)
        )
      } finally {
        setIsStreaming(false)
      }
    },
    [
      zoneContext,
      zoningService,
      language,
      sessionId,
      isStreaming,
      documentFilters,
    ]
  )

  const submitFeedback = useCallback(
    async (
      messageId: string,
      rating: number,
      feedbackType?:
        | 'helpful'
        | 'not_helpful'
        | 'incorrect'
        | 'incomplete'
        | 'other',
      comment?: string
    ) => {
      if (!zoningService.submitFeedback) {
        console.warn(
          '[ZoneQA] Feedback submission not available - submitFeedback not defined on service'
        )
        return
      }

      try {
        await zoningService.submitFeedback(
          messageId,
          rating,
          feedbackType,
          comment
        )
      } catch (err) {
        console.error('[ZoneQA] Error submitting feedback:', err)
        throw err
      }
    },
    [zoningService]
  )

  return {
    messages,
    isStreaming,
    error,
    isOpen,
    sessionId,
    sendMessage,
    toggleOpen,
    clearError,
    clearMessages,
    submitFeedback,
  }
}
